import { SITE_URL, BRAND_NAME, TWITTER_HANDLE, DEFAULT_OG_ALT, AUTHOR_NAME } from "./seo-constants";
import { sanityOgImageUrl } from "./sanity-image";
import { serializeJsonLd } from "./structured-data";
import { OG_IMAGES } from "./og-images.generated";

const DEFAULT_OG_IMAGE = "/og/default.png";
const DEFAULT_OG_WIDTH = 1200;
const DEFAULT_OG_HEIGHT = 630;
const DEFAULT_OG_TYPE = "image/png";

type SanityImageAsset = { asset: { url: string } };

export type SanityPostMeta = {
  title?: string;
  excerpt?: string;
  publishedAt?: string;
  _updatedAt?: string;
  meta_title?: string;
  meta_description?: string;
  keywords?: string[];
  canonical_url?: string;
  og_title?: string;
  og_description?: string;
  og_type?: "article" | "website";
  og_image?: SanityImageAsset;
  twitter_title?: string;
  twitter_description?: string;
  twitter_card_type?: "summary" | "summary_large_image" | "player" | "app";
  coverImage?: SanityImageAsset;
  authorName?: string;
};

type SeoInput = {
  title: string;
  description: string;
  path: string;
  /** Explicit canonical URL override (absolute). Takes precedence over `path`. */
  canonical?: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageType?: string;
  type?: "website" | "article" | "product";
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
  jsonLd?: object | object[];
  sanityMeta?: SanityPostMeta;
};

function clamp(s: string, max: number): string {
  if (s.length <= max) return s;
  if (import.meta.env.DEV) {
    console.warn(
      `[seo] description too long (${s.length} chars, max ${max}): "${s.slice(0, 60)}…"`,
    );
  }
  return s.slice(0, max - 1) + "…";
}

function buildCanonical(path: string, override?: string): string {
  if (override) return override;
  const stripped = path.replace(/\/$/, "") || "/";
  return `${SITE_URL}${stripped}`;
}

function buildTitle(pageTitle: string): string {
  if (pageTitle.endsWith(` — ${BRAND_NAME}`)) return pageTitle;
  return `${pageTitle} — ${BRAND_NAME}`;
}

function resolveImage(input: SeoInput): {
  url: string;
  width: number;
  height: number;
  type: string;
  alt: string;
} {
  const sm = input.sanityMeta;

  if (sm) {
    const src = sm.og_image ?? sm.coverImage;
    if (src) {
      const t = sanityOgImageUrl(src);
      return { ...t, alt: input.imageAlt ?? DEFAULT_OG_ALT };
    }
  }

  if (input.image) {
    const url = input.image.startsWith("http") ? input.image : `${SITE_URL}${input.image}`;
    return {
      url,
      width: input.imageWidth ?? DEFAULT_OG_WIDTH,
      height: input.imageHeight ?? DEFAULT_OG_HEIGHT,
      type: input.imageType ?? DEFAULT_OG_TYPE,
      alt: input.imageAlt ?? DEFAULT_OG_ALT,
    };
  }

  // Build-time generated card for static / locally-data-driven pages.
  // See scripts/generate-og-images.tsx and og-images.generated.ts.
  const generated = OG_IMAGES[input.path.replace(/\/$/, "") || "/"];
  if (generated) {
    return {
      url: `${SITE_URL}${generated}`,
      width: DEFAULT_OG_WIDTH,
      height: DEFAULT_OG_HEIGHT,
      type: DEFAULT_OG_TYPE,
      alt: input.imageAlt ?? DEFAULT_OG_ALT,
    };
  }

  return {
    url: `${SITE_URL}${DEFAULT_OG_IMAGE}`,
    width: DEFAULT_OG_WIDTH,
    height: DEFAULT_OG_HEIGHT,
    type: DEFAULT_OG_TYPE,
    alt: input.imageAlt ?? DEFAULT_OG_ALT,
  };
}

export function seo(input: SeoInput): {
  meta: object[];
  links: object[];
  scripts: object[];
} {
  const sm = input.sanityMeta;

  const rawTitle = sm ? (sm.meta_title ?? sm.title ?? input.title) : input.title;

  const rawDesc = sm ? (sm.meta_description ?? sm.excerpt ?? input.description) : input.description;

  const title = buildTitle(rawTitle);
  const description = clamp(rawDesc, 160);
  const canonical = buildCanonical(input.path, input.canonical ?? sm?.canonical_url);

  const ogTitle = sm ? (sm.og_title ?? sm.meta_title ?? sm.title ?? rawTitle) : rawTitle;
  const ogDesc = sm ? (sm.og_description ?? sm.meta_description ?? sm.excerpt ?? rawDesc) : rawDesc;
  const ogType = sm ? (sm.og_type ?? "article") : (input.type ?? "website");

  const twitterTitle = sm ? (sm.twitter_title ?? ogTitle) : ogTitle;
  const twitterDesc = sm ? (sm.twitter_description ?? ogDesc) : ogDesc;
  const twitterCard = sm ? (sm.twitter_card_type ?? "summary_large_image") : "summary_large_image";

  const img = resolveImage(input);

  // Per-page noindex only (404s, thin content). Site-wide preview noindex is
  // applied at the Worker via the X-Robots-Tag header (see src/server.ts) —
  // the helper has no request context, so it cannot detect the preview host.
  const isNoindex = !!input.noindex;
  const robotsContent = isNoindex ? "noindex, follow" : "index, follow";

  const publishedTime = input.publishedTime ?? sm?.publishedAt ?? undefined;
  const modifiedTime = input.modifiedTime ?? sm?._updatedAt ?? undefined;
  const authorName = input.authorName ?? sm?.authorName ?? AUTHOR_NAME;

  const meta: object[] = [
    { title },
    { name: "description", content: description },
    { name: "author", content: authorName },
    { name: "robots", content: robotsContent },
    { property: "og:title", content: ogTitle },
    { property: "og:description", content: ogDesc },
    { property: "og:type", content: ogType },
    { property: "og:url", content: canonical },
    { property: "og:site_name", content: BRAND_NAME },
    { property: "og:locale", content: "en_US" },
    { property: "og:image", content: img.url },
    { property: "og:image:width", content: String(img.width) },
    { property: "og:image:height", content: String(img.height) },
    { property: "og:image:type", content: img.type },
    { property: "og:image:alt", content: img.alt },
    { name: "twitter:card", content: twitterCard },
    { name: "twitter:site", content: TWITTER_HANDLE },
    { name: "twitter:creator", content: TWITTER_HANDLE },
    { name: "twitter:title", content: twitterTitle },
    { name: "twitter:description", content: twitterDesc },
    { name: "twitter:image", content: img.url },
    { name: "twitter:image:alt", content: img.alt },
  ];

  if (ogType === "article" && publishedTime) {
    meta.push({ property: "article:published_time", content: publishedTime });
  }
  if (ogType === "article" && modifiedTime) {
    meta.push({ property: "article:modified_time", content: modifiedTime });
  }
  if (ogType === "article") {
    meta.push({ property: "article:author", content: authorName });
    meta.push({ property: "article:section", content: BRAND_NAME });
  }

  if (sm?.keywords?.length) {
    meta.push({ name: "keywords", content: sm.keywords.join(", ") });
  }

  const links: object[] = [{ rel: "canonical", href: canonical }];

  const scripts: object[] = [];

  // JSON-LD is always generated from the document's structured fields. The legacy
  // hand-written `schema_json_ld` blob in Sanity is no longer read: its nodes were
  // stale (dead /case-study-post/ URLs, misattributed authors, malformed dates).
  if (!isNoindex) {
    const jsonLdItems = input.jsonLd
      ? Array.isArray(input.jsonLd)
        ? input.jsonLd
        : [input.jsonLd]
      : [];

    for (const ld of jsonLdItems) {
      scripts.push({
        type: "application/ld+json",
        children: serializeJsonLd(ld),
      });
    }
  }

  return { meta, links, scripts };
}
